/** App router setup. Maps all the routes to their page components and builds the route tree. */
import { createRootRoute, createRoute, createRouter, Outlet } from "@tanstack/react-router"
import { lazy, Suspense } from "react"
import { Skeleton } from "./components/ui"
import IndexPage from "./routes/index"
import LoginPage from "./routes/login"
import RegisterPage from "./routes/register"
import WorkspacePage from "./routes/workspace"
import ProjectPage from "./routes/project"
import BoardPage from "./routes/board"
import BillingPage from "./routes/billing"
import MembersPage from "./routes/members"
import AuditPage from "./routes/audit"
import WebhooksPage from "./routes/webhooks"
import ProfilePage from "./routes/profile"
import InvitePage from "./routes/invite"
import ListPage from "./routes/list"
import CalendarPage from "./routes/calendar"
import TimelinePage from "./routes/timeline"
import MyTasksPage from "./routes/my-tasks"
import NotFoundPage from "./routes/not-found"
import SupportPage from "./routes/support"
import PrivacyPage from "./routes/privacy"
import TermsPage from "./routes/terms"

const rootRoute = createRootRoute({
  component: () => <Outlet />,
})

const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: IndexPage })
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/login", component: LoginPage })
const registerRoute = createRoute({ getParentRoute: () => rootRoute, path: "/register", component: RegisterPage })
const workspaceRoute = createRoute({ getParentRoute: () => rootRoute, path: "/workspaces/$workspaceId", component: WorkspacePage })
const projectRoute = createRoute({ getParentRoute: () => rootRoute, path: "/workspaces/$workspaceId/projects/$projectId", component: ProjectPage })
const boardRoute = createRoute({ getParentRoute: () => rootRoute, path: "/workspaces/$workspaceId/projects/$projectId/boards/$boardId", component: BoardPage })
const listRoute = createRoute({ getParentRoute: () => rootRoute, path: "/workspaces/$workspaceId/projects/$projectId/boards/$boardId/list", component: ListPage })
const calendarRoute = createRoute({ getParentRoute: () => rootRoute, path: "/workspaces/$workspaceId/projects/$projectId/boards/$boardId/calendar", component: CalendarPage })
const timelineRoute = createRoute({ getParentRoute: () => rootRoute, path: "/workspaces/$workspaceId/projects/$projectId/boards/$boardId/timeline", component: TimelinePage })
const myTasksRoute = createRoute({ getParentRoute: () => rootRoute, path: "/my-tasks", component: MyTasksPage })
const billingRoute = createRoute({ getParentRoute: () => rootRoute, path: "/workspaces/$workspaceId/billing", component: BillingPage })
const membersRoute = createRoute({ getParentRoute: () => rootRoute, path: "/workspaces/$workspaceId/members", component: MembersPage })
const auditRoute = createRoute({ getParentRoute: () => rootRoute, path: "/workspaces/$workspaceId/audit", component: AuditPage })
const webhooksRoute = createRoute({ getParentRoute: () => rootRoute, path: "/workspaces/$workspaceId/webhooks", component: WebhooksPage })
const profileRoute = createRoute({ getParentRoute: () => rootRoute, path: "/profile", component: ProfilePage })
const inviteRoute = createRoute({ getParentRoute: () => rootRoute, path: "/invite/$token", component: InvitePage })
const supportRoute = createRoute({ getParentRoute: () => rootRoute, path: "/support", component: SupportPage })
const privacyRoute = createRoute({ getParentRoute: () => rootRoute, path: "/privacy", component: PrivacyPage })
const termsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/terms", component: TermsPage })
/** The landing page is lazy-loaded so the initial bundle stays small. */
const LandingPage = lazy(() => import("./routes/landing"))
const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/landing",
  component: () => (
    <Suspense fallback={
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="w-64 space-y-3">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
    }>
      <LandingPage />
    </Suspense>
  ),
})
const notFoundRoute = createRoute({ getParentRoute: () => rootRoute, path: "*", component: NotFoundPage })

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  landingRoute,
  workspaceRoute,
  projectRoute,
  boardRoute,
  listRoute,
  calendarRoute,
  timelineRoute,
  myTasksRoute,
  billingRoute,
  membersRoute,
  auditRoute,
  webhooksRoute,
  profileRoute,
  inviteRoute,
  supportRoute,
  privacyRoute,
  termsRoute,
  notFoundRoute,
])

export const router = createRouter({ routeTree })

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}
